import { useStyletron } from 'parkdepot-shared/theme'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal'
import { Options, Select, Value } from 'baseui/select'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import FormPlateInput from 'parkdepot-shared/components/LPInput'
import useLicensePlateValidation from 'parkdepot-shared/utils/hooks/useLicensePlateValidation'
import React, { ChangeEvent, FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { VehicleTypesOptions } from '../../constants/vehicleTypes.consts'
import { Vehicle } from '../../types/vehicle.interface'
import InfoComponent from '../InfoComponent/InfoComponent.component'
import {
    ButtonContainer,
    Container,
    InputContainer,
    InputPlaceholder,
    ModalBodyContent,
    OptionIcon,
    OptionText,
    PlateError,
    SelectDownArrow,
    SubmitButtonContainer,
    Title,
    TitleContainer,
    ValueLabel,
    Label,
    SelectOverrides,
    BaseButtonOverrides
} from './MyVehicleForm.style'
import { getDomainExtension } from '@utils/getDomainExtension'

interface Props {
    vehicle?: Vehicle | null
    loading: boolean
    dropdownValue?: Value
    inDialog?: boolean
    onSubmitClick: (vehicle: Vehicle) => Promise<boolean>
    onCancelClick: () => void
    onDeleteClick: (vehicle: Vehicle) => void
}

const MyVehicleForm: FC<Props> = ({
    vehicle,
    dropdownValue,
    loading,
    inDialog = false,
    onSubmitClick,
    onDeleteClick
}) => {
    const [css, theme] = useStyletron()
    const { t } = useTranslation()
    const validateLicensePlate = useLicensePlateValidation()
    const [updatedVehicle, setUpdatedVehicle] = useState<Vehicle | undefined | null>(vehicle)
    const [updatedDropdownValue, setUpdatedDropdownValue] = useState<Value | undefined>(dropdownValue)
    const [vehicleOptions, setVehicleOptions] = useState<Options>([...VehicleTypesOptions(t)])
    const [editable, setEditable] = useState(true)
    const [changed, setChanged] = useState(false)
    const [enableConfirmModal, setEnableConfirmModal] = useState(false)
    const [confirmModalOpen, setConfirmModalOpen] = useState(false)
    const [plateError, setPlateError] = useState<string | null>(null)

    const domain = getDomainExtension()
    const defaultCountryPlate = domain === 'com' ? 'INT' : domain.toUpperCase()

    const onDropdownValueChange = (value: Value) => {
        setChanged(true)
        if (value.length === 0) {
            setUpdatedDropdownValue(undefined)
            return
        }
        setUpdatedDropdownValue(value)
    }

    const clearButtonFocus = () => {
        const button = document.getElementById('submit-button')
        if (button) {
            button.blur()
        }
    }

    useEffect(() => {
        // Ensure VehicleTypesOptions returns an array of options
        const updatedOptions: Options = VehicleTypesOptions(t) as Options
        setVehicleOptions(updatedOptions)

        // Preselect the first value if updatedDropdownValue is undefined and options exist
        if (updatedDropdownValue === undefined && Array.isArray(updatedOptions) && updatedOptions.length > 0) {
            setUpdatedDropdownValue([updatedOptions[0]])
        }
    }, [])

    useEffect(() => {
        if (loading) return

        // new vehicle
        if (!vehicle?.id && !vehicle?.name) {
            setUpdatedVehicle({
                name: '',
                manufacturer: '',
                model: '',
                vehicle_type: '',
                plate: '',
                country: defaultCountryPlate
            })
            return
        }

        // existing vehicle
        if (vehicle && vehicle.id) {
            setUpdatedVehicle(vehicle)
            setUpdatedDropdownValue([
                {
                    id: vehicle.vehicle_type?.toLowerCase(),
                    label: t(`profile.vehicle.vehicleTypes.${vehicle.vehicle_type?.toLowerCase()}`)
                }
            ])
            return
        }

        // vehicle populated from query params
        if (vehicle && vehicle.id === '' && vehicle.plate) {
            setChanged(true)
        }
    }, [vehicle, loading])

    const onButtonClick = async () => {
        // validate plate
        const plateError = validateLicensePlate(updatedVehicle?.plate ?? '', updatedVehicle?.country ?? '')
        if (plateError && !plateError.valid) {
            setPlateError(plateError.errors.plate || '')
            return
        }

        // if plate exists and changed
        if (confirmModalOpen) {
            onSubmitClick({
                ...updatedVehicle,
                vehicle_type: updatedDropdownValue && updatedDropdownValue[0].id?.toString().toUpperCase()
            } as Vehicle)
            setConfirmModalOpen(false)
            clearButtonFocus()
            return
        }

        // update vehicle or start editing
        if (editable && vehicle?.id) {
            const isSuccess = await onSubmitClick({
                ...updatedVehicle,
                vehicle_type: updatedDropdownValue && updatedDropdownValue[0].id?.toString().toUpperCase()
            } as Vehicle)

            if (!isSuccess) return

            if (enableConfirmModal) {
                setConfirmModalOpen(true)
                setEnableConfirmModal(false)
            }

            clearButtonFocus()

            return
        }

        // save new vehicle
        if (editable && !vehicle?.id) {
            await onSubmitClick({
                ...updatedVehicle,
                vehicle_type: updatedDropdownValue && updatedDropdownValue[0].id?.toString().toUpperCase()
            } as Vehicle)
            clearButtonFocus()
            return
        }

        setEditable(true)
    }

    const onInputValueChange = (e: ChangeEvent<HTMLInputElement>, key: string) => {
        setChanged(true)

        const value = e.target.value
        setUpdatedVehicle((v) => ({ ...v, [key]: value }))
    }

    return (
        <>
            <Container $noPadding={inDialog}>
                {!inDialog && (
                    <TitleContainer>
                        <Title>{t('profile.vehicle.myVehicle')}</Title>
                        <InfoComponent text={t('profile.infoText')} />
                    </TitleContainer>
                )}
                <div>
                    <InputPlaceholder>{t('common.plate')}*</InputPlaceholder>
                    {editable ? (
                        <FormPlateInput
                            value={updatedVehicle?.plate ?? ''}
                            onChange={(plate) => {
                                setPlateError(null)
                                setUpdatedVehicle((v) => ({ ...v, plate }))
                                setEnableConfirmModal(true)
                                setChanged(true)
                            }}
                            country={updatedVehicle?.country ?? ''}
                            onCountryChange={(country) => {
                                setUpdatedVehicle((v) => ({ ...v, country }))
                                setEnableConfirmModal(true)
                                setChanged(true)
                            }}
                        />
                    ) : (
                        <Label>{updatedVehicle?.plate}</Label>
                    )}
                    {plateError && <PlateError>{plateError}</PlateError>}
                </div>
                <div>
                    <InputPlaceholder>{t('profile.vehicle.vehicleName')}</InputPlaceholder>
                    {editable ? (
                        <InputContainer
                            type="text"
                            value={updatedVehicle?.name ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                onInputValueChange(e, 'name')
                            }}
                            placeholder={t('profile.vehicle.vehiclePlaceholder')}
                            $empty={!updatedVehicle?.name}
                        />
                    ) : (
                        <Label>{updatedVehicle?.name}</Label>
                    )}
                </div>
                <div>
                    <InputPlaceholder>{t('profile.vehicle.vehicleType')}</InputPlaceholder>
                    {editable ? (
                        <Select
                            placeholder={t('profile.vehicle.select')}
                            options={vehicleOptions}
                            labelKey="label"
                            valueKey="label"
                            clearable={false}
                            searchable={false}
                            onChange={({ value }) => onDropdownValueChange(value)}
                            value={updatedDropdownValue}
                            getOptionLabel={(value) => (
                                <div
                                    className={css({
                                        color:
                                            updatedDropdownValue &&
                                            updatedDropdownValue?.length > 0 &&
                                            updatedDropdownValue[0].id === value?.option.id
                                                ? theme.colors.primary
                                                : '#1F1F1F',
                                        display: 'flex',
                                        gap: '8px',
                                        alignItems: 'center'
                                    })}
                                >
                                    <OptionIcon className={value?.option?.icon} />
                                    <OptionText>{value?.option?.label}</OptionText>
                                </div>
                            )}
                            getValueLabel={(value) => (
                                <ValueLabel>
                                    <OptionIcon className={value?.option?.icon} />
                                    <OptionText>{value?.option?.label}</OptionText>
                                </ValueLabel>
                            )}
                            overrides={{
                                ...SelectOverrides,
                                SelectArrow: {
                                    component: () => <SelectDownArrow className="wemolo-chevron-down" />
                                }
                            }}
                        />
                    ) : (
                        <Label>{updatedDropdownValue && updatedDropdownValue[0].label}</Label>
                    )}
                </div>
                <ButtonContainer>
                    <Button
                        id="vehicle-action-button"
                        isLoading={loading}
                        disabled={
                            !updatedVehicle?.plate || !updatedDropdownValue || !updatedVehicle?.country || !changed
                        }
                        $size={BUTTON_SIZE.medium}
                        $kind={editable ? BUTTON_KIND.primary : BUTTON_KIND.secondary}
                        overrides={{
                            BaseButton: {
                                style: {
                                    width: '100%'
                                }
                            }
                        }}
                        onClick={onButtonClick}
                    >
                        <div>
                            {editable && updatedVehicle?.plate_id && t('profile.vehicle.saveYourChanges')}
                            {editable && !updatedVehicle?.plate_id && t('profile.vehicle.add')}
                            {!editable && updatedVehicle?.plate_id && t('profile.changeVehicle')}
                            {!editable && !updatedVehicle?.plate_id && t('profile.vehicle.add')}
                        </div>
                    </Button>
                    {vehicle?.id && (
                        <Button
                            id="delete-your-vehicle-btn"
                            $size={BUTTON_SIZE.medium}
                            $kind={BUTTON_KIND.secondary}
                            overrides={BaseButtonOverrides}
                            onClick={() => {
                                onDeleteClick(vehicle)
                            }}
                        >
                            <div>{t('profile.vehicle.deleteModal.confirm')}</div>
                        </Button>
                    )}
                </ButtonContainer>
            </Container>
            <Modal isOpen={confirmModalOpen} closeable={false}>
                <ModalHeader
                    className={css({
                        ...theme.typography.HeadingSmall,
                        margin: '0.8rem 0',
                        textAlign: 'center'
                    })}
                >
                    {t('profile.vehicle.confirmModal.title')} ☝️
                </ModalHeader>
                <ModalBody>
                    <ModalBodyContent>{t('profile.vehicle.confirmModal.description')}</ModalBodyContent>
                </ModalBody>
                <ModalFooter>
                    <SubmitButtonContainer>
                        <Button
                            id="submit-button"
                            $size={BUTTON_SIZE.medium}
                            $kind={BUTTON_KIND.primary}
                            overrides={{
                                BaseButton: {
                                    style: {
                                        width: '100%'
                                    }
                                }
                            }}
                            onClick={() => {
                                setConfirmModalOpen(false)
                            }}
                        >
                            <div>
                                {t('profile.vehicle.confirmModal.confirmButtonText')}{' '}
                                <span role="img" aria-label="confirm">
                                    🤓
                                </span>
                            </div>
                        </Button>
                    </SubmitButtonContainer>
                </ModalFooter>
            </Modal>
        </>
    )
}

export default MyVehicleForm
